import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'

export type ImportType = 'company_only' | 'tag' | 'deal'

export type ImportDataRowsFormat = {
  headers: { original: string; mapped: string }[]
  rows: {
    columns: string[]
    rows: any[][]
  }
}

export type ProcessRunning =
  | 'none'
  | 'creating'
  | 'matching'
  | 'merging'
  | 'setting_companies'
  | 'setting_deals'
  | 'setting_tags'
  | 'setting_gpos'
  | 'setting_contacts'
  | 'setting_notes'

export const PROCESS_RUNNING_LABELS: Record<ProcessRunning, string> = {
  none: 'Processing',
  creating: 'Creating import preview',
  matching: 'Matching companies',
  merging: 'Merging chains',
  setting_companies: 'Setting up companies',
  setting_deals: 'Importing deals',
  setting_tags: 'Importing tags',
  setting_gpos: 'Importing GPOs',
  setting_contacts: 'Importing contacts',
  setting_notes: 'Importing company notes',
}

export interface ImportPreview {
  id: number
  import_number: number
  name?: string
  status: 'idle' | 'running'
  process_running: ProcessRunning | null
  created: string
  modified: string
  user: number
  raw_data: string
  type: ImportType
  data: ImportData
}

export interface ImportPreviewDB {
  id: number
  import_number: number
  name: string
  from_unmatched_chains: boolean
  skip_matching: boolean
  status: 'idle' | 'running'
  process_running: ProcessRunning | null
  created: string
  modified: string
  user: number
  raw_data: string
  type: ImportType
  data: ImportDataRowsFormat
}

export type ImportPreviewNoData = Omit<ImportPreview, 'data' | 'raw_data'>

export type DealValidationColumnName =
  | 'deal_product'
  | 'deal_product_variant'
  | 'deal_account_owner'
  | 'deal_sales_stage'

export type ColumnType =
  | 'primary_key'
  | 'company_match'
  | 'tag_data'
  | 'gpo_data'
  | 'deal_data'
  | 'contact_data'
  | 'company_notes'
  | 'display_only'
  | 'ignore'

export enum InternalColumns {
  ROW_ID = '__internal__row_id',
  EXISTING_CHAIN_PROXY_ID = '__internal__existing_chain_proxy_id',
  EXISTING_CHAIN_PROXY_TYPE = '__internal__existing_chain_proxy_type',
  EXISTING_CHAIN_PROXY_SUBCATEGORY_ID = '__internal__existing_chain_proxy_subcategory_id',
  SET_CHAIN_PROXY_ID = '__internal__set_chain_proxy_id',
  MATCHED_DOOR = '__internal__matched_door',
  MATCHED_CHAIN = '__internal__matched_chain',
  SUGGESTED_DOORS = '__internal__suggested_doors',
  SUGGESTED_CHAINS = '__internal__suggested_chains',
  IS_CUSTOM_COMPANY = '__internal__is_custom_company',
  CUSTOM_COMPANY_CATEGORY_ID = '__internal__custom_company_category_id',
  CUSTOM_COMPANY_SUB_CATEGORY_ID = '__internal__custom_company_sub_category_id',
  READY = '__internal__ready',
  IS_CHAIN_MERGED = '__internal__is_chain_merged',
  IS_DOOR_MANUAL_MATCH = '__internal__is_door_manual_match',
  IS_CHAIN_MANUAL_MATCH = '__internal__is_chain_manual_match',
  TAG_SET = '__internal__tag_set',
  GPO_SET = '__internal__gpo_set',
  DEAL_SET = '__internal__deal_set',
  CONTACT_SET = '__internal__contact_set',
  COMPANY_NOTES_SET = '__internal__company_notes_set',
  DEAL_PRODUCT = '__internal__deal_product',
  MATCHED_CHAIN_PK = '__internal__matched_chain_pk',
  MATCHED_CHAIN_TYPE = '__internal__matched_chain_type',
  REVIEWED = '__internal__reviewed',
  COMPANY_HASH = '__internal__company_hash',
  CONTACT_HASH = '__internal__contact_hash',
  FAILED_IMPORT_REASON = '__internal__failed_import_reason',
  IS_EXISTING_CRM_CONTACT = '__internal__is_existing_crm_contact',
}

export type CSVRow = {
  [key: string]: any
  [InternalColumns.ROW_ID]: string
  [InternalColumns.EXISTING_CHAIN_PROXY_ID]: number
  [InternalColumns.EXISTING_CHAIN_PROXY_TYPE]: string
  [InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID]: number
  [InternalColumns.SET_CHAIN_PROXY_ID]: number
  [InternalColumns.MATCHED_DOOR]: DoorSearchResult | null
  [InternalColumns.MATCHED_CHAIN]: ChainSearchResult | null
  [InternalColumns.SUGGESTED_DOORS]: DoorSearchResult[] | null
  [InternalColumns.SUGGESTED_CHAINS]: ChainSearchResult[] | null
  [InternalColumns.IS_CUSTOM_COMPANY]: boolean
  [InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]: number | null
  [InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]: number | null
  [InternalColumns.READY]: boolean
  [InternalColumns.IS_CHAIN_MERGED]: boolean
  [InternalColumns.IS_DOOR_MANUAL_MATCH]: boolean
  [InternalColumns.IS_CHAIN_MANUAL_MATCH]: boolean
  [InternalColumns.TAG_SET]: number
  [InternalColumns.GPO_SET]: number
  [InternalColumns.DEAL_SET]: number
  [InternalColumns.CONTACT_SET]: number
  [InternalColumns.COMPANY_NOTES_SET]: number
  [InternalColumns.DEAL_PRODUCT]?: string
  [InternalColumns.MATCHED_CHAIN_PK]: number
  [InternalColumns.MATCHED_CHAIN_TYPE]: string
  [InternalColumns.REVIEWED]: boolean
  [InternalColumns.COMPANY_HASH]: string
  [InternalColumns.CONTACT_HASH]: number
  [InternalColumns.FAILED_IMPORT_REASON]: string
  [InternalColumns.IS_EXISTING_CRM_CONTACT]: boolean
}

export type CSVRowState = 'pending_review' | 'reviewed'

export interface HeaderRow {
  original: string
  mapped: string
  label?: string
}

export type ImportData = {
  rows: CSVRow[]
  headers: HeaderRow[]
  skipMatching?: boolean
}

export type ColumnColors = {
  background: string
  text: string
}

export type ColumnOption = {
  name: string
  type: ColumnType
}
